import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import './Disclaimer.css';

interface DisclaimerPageItem {
  labelKey: string;
  textKey: string;
  defaultLabel: string;
  isHighlighted?: boolean;
}

const DISCLAIMER_PAGE_ITEMS: DisclaimerPageItem[] = [
  {
    labelKey: 'aiDataSharingLabel',
    textKey: 'aiDataSharingText',
    defaultLabel: 'AI & Third-Party Data Sharing:',
    isHighlighted: true,
  },
  {
    labelKey: 'aiProtectionLabel',
    textKey: 'aiProtectionText',
    defaultLabel: 'Data Protection & Equal Privacy:',
    isHighlighted: true,
  },
  {
    labelKey: 'representativeLabel',
    textKey: 'representativeText',
    defaultLabel: 'Representative Nature:',
  },
  {
    labelKey: 'notRulingLabel',
    textKey: 'notRulingText',
    defaultLabel: 'Not a Religious Ruling:',
  },
  {
    labelKey: 'hallucinationLabel',
    textKey: 'hallucinationText',
    defaultLabel: 'Margin of Error (Hallucination):',
  },
  {
    labelKey: 'chronologicalLabel',
    textKey: 'chronologicalText',
    defaultLabel: 'Chronological Inconsistencies:',
  },
  {
    labelKey: 'modernLabel',
    textKey: 'modernText',
    defaultLabel: 'Modern Concepts:',
  },
  {
    labelKey: 'styleLabel',
    textKey: 'styleText',
    defaultLabel: 'Style Limitations:',
  },
  {
    labelKey: 'sessionBillingLabel',
    textKey: 'sessionBillingText',
    defaultLabel: 'Session Minutes & Credits:',
  },
  {
    labelKey: 'sponsorshipLabel',
    textKey: 'sponsorshipText',
    defaultLabel: 'Sponsorship:',
  },
  {
    labelKey: 'disclaimerLabel',
    textKey: 'disclaimerText',
    defaultLabel: 'Disclaimer:',
  },
];

const Disclaimer: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="disclaimer-page">
      <div className="bg-accent-shape" />
      <Header />

      <main className="disclaimer-main">
        <section className="disclaimer-hero">
          <div className="disclaimer-hero-content">
            <div className="hero-line" />
            <h2 className="hero-label">{t('disclaimer.heroLabel', 'Legal')}</h2>
            <div className="hero-line" />
          </div>
          <h1 className="hero-title">{t('disclaimer.title', 'Disclaimer')}</h1>
        </section>

        <section className="disclaimer-content">
          <p className="disclaimer-intro-text">{t('disclaimer.intro')}</p>

          <div className="disclaimer-items">
            {DISCLAIMER_PAGE_ITEMS.map((item) => (
              <div
                key={item.labelKey}
                className={`disclaimer-page-item ${item.isHighlighted ? 'disclaimer-page-item-highlighted' : ''}`}
              >
                <span
                  className={`disclaimer-page-label ${item.isHighlighted ? 'disclaimer-page-label-highlighted' : ''}`}
                >
                  {t(`disclaimer.${item.labelKey}`, item.defaultLabel)}
                </span>
                <span className="disclaimer-page-text"> {t(`disclaimer.${item.textKey}`)}</span>
              </div>
            ))}
          </div>

          <div className="disclaimer-page-divider" />

          {/* Legal Links */}
          <div className="disclaimer-page-links">
            <Link to="/privacy" className="disclaimer-page-link">
              {t('privacy.title', 'Privacy Policy')}
            </Link>
            <span className="disclaimer-page-link-divider">•</span>
            <Link to="/terms" className="disclaimer-page-link">
              {t('terms.title', 'Terms of Use')}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Disclaimer;
